import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, LogOut, Plus, Users, CheckCircle, Clock, Phone, Mail, 
  DollarSign, User, Briefcase, Home, Shield, TrendingUp
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  'Received': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  'Assigned to Agent': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  'Re-assigned': 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
  'Contacted': 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  'Appointment Set': 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  'Success': 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  'Did not proceed': 'bg-red-500/20 text-red-300 border-red-500/30',
  'Paid': 'bg-green-500/20 text-green-300 border-green-500/30'
};

const businessTypeIcons = {
  'Mortgage': Home,
  'Insurance': Shield,
  'Investments': TrendingUp
};

export default function ReferrerDashboard() {
  const navigate = useNavigate();
  const [referrer, setReferrer] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    business_type: '',
    referral_name: '',
    referral_email: '',
    referral_phone: '',
    notes: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('referrer_token');
    const referrerData = localStorage.getItem('referrer');
    
    if (!token || !referrerData) {
      navigate('/referrer/login');
      return;
    }

    setReferrer(JSON.parse(referrerData));
    fetchReferrals(token);
  }, [navigate]);

  const fetchReferrals = async (token) => {
    try {
      const response = await axios.get(`${API}/api/referrals/my-referrals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferrals(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('referrer_token');
        localStorage.removeItem('referrer');
        navigate('/referrer/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('referrer_token');
    localStorage.removeItem('referrer');
    navigate('/referrer/login');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('referrer_token');
      await axios.post(`${API}/api/referrals`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Referral submitted successfully!');
      setShowModal(false);
      setFormData({
        business_type: '',
        referral_name: '',
        referral_email: '',
        referral_phone: '',
        notes: ''
      });
      fetchReferrals(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit referral');
    } finally {
      setSubmitting(false);
    }
  };

  const stats = {
    total: referrals.length,
    pending: referrals.filter(r => !['Success', 'Paid', 'Did not proceed'].includes(r.status)).length,
    successful: referrals.filter(r => ['Success', 'Paid'].includes(r.status)).length,
    totalEarnings: referrals.filter(r => ['Success', 'Paid'].includes(r.status)).length * 200,
    pendingPayment: referrals.filter(r => (r.status === 'Success' || r.status === 'Paid') && !r.referral_fee_paid).length,
    paidOut: referrals.filter(r => r.referral_fee_paid).reduce((sum, r) => sum + r.referral_fee_amount, 0)
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 border-b border-slate-700 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Utmost Financial Inc.</h1>
              <p className="text-xs text-slate-400">Referral Partner Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium text-white">{referrer?.name}</p>
              <p className="text-xs text-slate-400">{referrer?.email}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} className="text-slate-400 hover:text-white">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total}</p>
                  <p className="text-xs text-slate-400">Total Referrals</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.pending}</p>
                  <p className="text-xs text-slate-400">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.successful}</p>
                  <p className="text-xs text-slate-400">Successful</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">${stats.paidOut}</p>
                  <p className="text-xs text-slate-400">Earned</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white">My Referrals</h2>
          <Dialog open={showModal} onOpenChange={setShowModal}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Submit New Referral
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
              <DialogHeader>
                <DialogTitle>Submit a New Referral</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Business Type</Label>
                  <Select 
                    value={formData.business_type} 
                    onValueChange={(value) => setFormData({ ...formData, business_type: value })}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600">
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      <SelectItem value="Mortgage">🏠 Mortgage</SelectItem>
                      <SelectItem value="Insurance">🛡️ Insurance</SelectItem>
                      <SelectItem value="Investments">📈 Investments</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Referral's Full Name</Label>
                  <Input
                    value={formData.referral_name}
                    onChange={(e) => setFormData({ ...formData, referral_name: e.target.value })}
                    placeholder="John Smith"
                    className="bg-slate-700 border-slate-600"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Referral's Email</Label>
                  <Input
                    type="email"
                    value={formData.referral_email}
                    onChange={(e) => setFormData({ ...formData, referral_email: e.target.value })}
                    placeholder="john@example.com"
                    className="bg-slate-700 border-slate-600"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Referral's Phone</Label>
                  <Input
                    type="tel"
                    value={formData.referral_phone}
                    onChange={(e) => setFormData({ ...formData, referral_phone: e.target.value })}
                    placeholder="(204) 555-0123"
                    className="bg-slate-700 border-slate-600"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Notes (Optional)</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Any additional information..."
                    className="bg-slate-700 border-slate-600"
                    rows={3}
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  disabled={submitting || !formData.business_type}
                >
                  {submitting ? 'Submitting...' : 'Submit Referral'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Referrals List */}
        {referrals.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Referrals Yet</h3>
              <p className="text-slate-400 mb-6">Start earning $200 per successful referral!</p>
              <Button onClick={() => setShowModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Submit Your First Referral
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {referrals.map((referral) => {
              const TypeIcon = businessTypeIcons[referral.business_type] || Briefcase;
              return (
                <Card key={referral.id} className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-slate-700/50 flex items-center justify-center flex-shrink-0">
                          <TypeIcon className="w-6 h-6 text-slate-300" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white">{referral.referral_name}</h3>
                          <div className="flex flex-wrap gap-2 mt-1">
                            <Badge variant="outline" className="bg-slate-700/50 text-slate-300 border-slate-600">
                              {referral.business_type}
                            </Badge>
                            <Badge variant="outline" className={statusColors[referral.status]}>
                              {referral.status}
                            </Badge>
                            {referral.referral_fee_paid && (
                              <Badge variant="outline" className="bg-green-500/20 text-green-300 border-green-500/30">
                                💰 $200 Paid
                              </Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-400">
                            <span className="flex items-center gap-1">
                              <Mail className="w-4 h-4" />
                              {referral.referral_email}
                            </span>
                            <span className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              {referral.referral_phone}
                            </span>
                          </div>
                          {referral.assigned_agent_name && (
                            <p className="text-sm text-emerald-400 mt-2 flex items-center gap-1">
                              <User className="w-4 h-4" />
                              Assigned to: {referral.assigned_agent_name}
                            </p>
                          )}
                          {referral.notes && (
                            <p className="text-sm text-slate-500 mt-2 italic">"{referral.notes}"</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">
                          Submitted: {new Date(referral.created_at).toLocaleDateString()}
                        </p>
                        {referral.status === 'Success' && !referral.referral_fee_paid && (
                          <p className="text-sm text-amber-400 mt-1">$200 Payment Pending</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
